import type { OperatingSystemRuntimeStorageSeed } from "@/lib/operatingSystemRuntimeStorage";
import { injectRuntimeNavigationSeed, runtimeNavigationScript, runtimeRouterModule, type RuntimeNavigationSnapshot } from "./operatingSystemRuntimeNavigation";
import { rewriteRuntimeNavigationSource } from "../../supabase/functions/_shared/osRuntimeNavigation";

const PASS_THROUGH_URL_PATTERN = /^(?:about|blob|data|javascript|mailto|tel):/i;
const RUNTIME_ROOT_PATH_PATTERN = /^\/(?:@vite\/client|@react-refresh|@fs(?:\/|$)|@id(?:\/|$)|api(?:\/|$)|app(?:\/|$)|assets(?:\/|$)|base44-vite-deps(?:\/|$)|node_modules(?:\/|$)|src(?:\/|$))/;

function scriptJson(value: unknown): string {
  return JSON.stringify(value)
    .replace(/&/g, "\\u0026")
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

export function operatingSystemRuntimeProxyBaseUrl(runtimeUrl: string): string | null {
  try {
    const url = new URL(runtimeUrl);
    url.hash = "";
    return url.toString().replace(/\/$/, "");
  } catch {
    return null;
  }
}

export function toOperatingSystemRuntimeProxyUrl(value: string, proxyBaseUrl: string): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith("#") || PASS_THROUGH_URL_PATTERN.test(trimmed)) return value;
  if (trimmed.startsWith(`${proxyBaseUrl}/`)) return trimmed;
  if (trimmed.startsWith("/") && !trimmed.startsWith("//") && RUNTIME_ROOT_PATH_PATTERN.test(trimmed)) {
    return `${proxyBaseUrl}${trimmed}`;
  }
  return value;
}

/** Ticket-scoped startup assets that must answer before the iframe executes. */
export function runtimeBootstrapAssetUrls(html: string, runtimeUrl: string): string[] {
  const proxyBase = operatingSystemRuntimeProxyBaseUrl(runtimeUrl);
  if (!proxyBase) return [];
  const urls = new Set<string>();
  const tags = /<(script|link)\b([^>]*)>/gi;
  const attributes = /([\w:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/gi;
  let match: RegExpExecArray | null;
  while ((match = tags.exec(html)) !== null && urls.size < 8) {
    const attrs = new Map<string, string>();
    for (const attribute of match[2].matchAll(attributes)) {
      attrs.set(attribute[1].toLowerCase(), attribute[2] ?? attribute[3] ?? attribute[4] ?? "");
    }
    const isScript = match[1].toLowerCase() === "script";
    const rel = (attrs.get("rel") ?? "").toLowerCase().split(/\s+/);
    if (!isScript && !rel.includes("stylesheet") && !rel.includes("modulepreload")) continue;
    const scriptType = (attrs.get("type") ?? "").trim().toLowerCase();
    if (isScript && scriptType && !/^(?:module|(?:text|application)\/(?:javascript|ecmascript|x-javascript))$/.test(scriptType)) continue;
    const raw = (attrs.get(isScript ? "src" : "href") ?? "").trim();
    if (!raw || raw.startsWith("//") || PASS_THROUGH_URL_PATTERN.test(raw)) continue;
    try {
      const resolved = new URL(toOperatingSystemRuntimeProxyUrl(raw, proxyBase), `${proxyBase}/`);
      if (resolved.origin !== new URL(proxyBase).origin || !resolved.href.startsWith(`${proxyBase}/`)) continue;
      const path = resolved.href.slice(proxyBase.length);
      if (/^\/(?:@vite\/client|@react-refresh)(?:[?#]|$)/.test(path) ||
          /^\/(?:src|app|assets|node_modules|base44-vite-deps)\/[^?#]+\.(?:js|mjs|jsx|ts|tsx|css)(?:[?#]|$)/.test(path)) {
        urls.add(resolved.href);
      }
    } catch {
      // Ignore malformed or non-ticket URLs; iframe error recovery remains active.
    }
  }
  return [...urls];
}

export function isRuntimeBootstrapAssetReady(url: string, response: Response): boolean {
  if (response.status !== 200) return false;
  const contentType = response.headers.get("content-type")?.split(";", 1)[0].trim().toLowerCase() ?? "";
  return /\.css(?:[?#]|$)/i.test(url)
    ? contentType === "text/css"
    : /^(?:text|application)\/(?:javascript|ecmascript|x-javascript)$/.test(contentType);
}

function rewriteSrcset(value: string, proxyBaseUrl: string): string {
  return value.split(",").map((candidate) => {
    const parts = candidate.trim().split(/\s+/);
    const url = parts.shift();
    if (!url) return candidate;
    return [toOperatingSystemRuntimeProxyUrl(url, proxyBaseUrl), ...parts].join(" ");
  }).join(", ");
}

function runtimeImportMapScript(proxyBaseUrl: string, extraImports: Record<string, string>): string {
  const imports = {
    "/@vite/client": `${proxyBaseUrl}/@vite/client`,
    "/@react-refresh": `${proxyBaseUrl}/@react-refresh`,
    "/@fs/": `${proxyBaseUrl}/@fs/`,
    "/@id/": `${proxyBaseUrl}/@id/`,
    "/app/": `${proxyBaseUrl}/app/`,
    "/assets/": `${proxyBaseUrl}/assets/`,
    "/node_modules/": `${proxyBaseUrl}/node_modules/`,
    "/src/": `${proxyBaseUrl}/src/`,
    ...extraImports,
  };
  return `<script type="importmap" data-applyt-runtime-importmap="true">${scriptJson({ imports })}</script>`;
}

function runtimeLayoutCompatibilityStyle(): string {
  return `<style data-applyt-runtime-layout-compatibility="true">
@media (min-width: 1024px) {
  [class~="pt-14"][class~="lg:pt-0"] {
    padding-top: 0 !important;
  }
}
</style>`;
}

function runtimeBootstrapScript(proxyBaseUrl: string, seed: OperatingSystemRuntimeStorageSeed): string {
  return `<script data-applyt-runtime-embed-bootstrap="true">
(function(){
  if (window.__APPLYT_RUNTIME_EMBED_BOOTSTRAP__) return;
  window.__APPLYT_RUNTIME_EMBED_BOOTSTRAP__ = true;
  var proxyBase=${scriptJson(proxyBaseUrl)};
  var seed=${scriptJson(seed)};
  var runtimeToken="applyt_runtime_proxy";
  var rootPathPattern=/^\\/(?:@vite\\/client|@react-refresh|@fs(?:\\/|$)|@id(?:\\/|$)|api(?:\\/|$)|app(?:\\/|$)|assets(?:\\/|$)|node_modules(?:\\/|$)|src(?:\\/|$))/;
  function record(value){return value&&typeof value==="object"&&!Array.isArray(value)?value:{};}
  function seedStorage(storage, values){
    var data=record(values);
    Object.keys(data).forEach(function(key){try{storage.setItem(key,String(data[key]));}catch(_error){}});
    try{storage.setItem("base44_access_token",runtimeToken);}catch(_error){}
    try{storage.setItem("token",runtimeToken);}catch(_error){}
  }
  function createStorage(name){
    var store=Object.assign({},record(seed[name]));
    store.base44_access_token=runtimeToken;
    store.token=runtimeToken;
    return {
      get length(){return Object.keys(store).length;},
      clear:function(){store={base44_access_token:runtimeToken,token:runtimeToken};},
      getItem:function(key){key=String(key);return Object.prototype.hasOwnProperty.call(store,key)?String(store[key]):null;},
      key:function(index){return Object.keys(store)[index]||null;},
      removeItem:function(key){delete store[String(key)];},
      setItem:function(key,value){store[String(key)]=String(value);}
    };
  }
  function installStorage(name){
    try{
      var storage=window[name];
      var probe="__applyt_runtime_storage_probe__";
      storage.setItem(probe,"1");
      storage.removeItem(probe);
      seedStorage(storage,seed[name]);
      return;
    }catch(_error){}
    try{Object.defineProperty(window,name,{configurable:true,value:createStorage(name)});}catch(_innerError){}
  }
  function proxiedUrl(value){
    if (typeof value!=="string") return value;
    var trimmed=value.trim();
    if (!trimmed||trimmed.charAt(0)==="#"||/^(?:about|blob|data|javascript|mailto|tel):/i.test(trimmed)) return value;
    if (trimmed.indexOf(proxyBase+"/")===0) return trimmed;
    if (trimmed.charAt(0)==="/"&&trimmed.indexOf("//")!==0&&rootPathPattern.test(trimmed)) return proxyBase+trimmed;
    return value;
  }
  if (typeof window.URL==="function"&&!window.URL.__applytRuntimeProxyWrapped){
    var NativeURL=window.URL;
    var RuntimeURL=function(input,base){
      var nextInput=typeof input==="string"?proxiedUrl(input):input;
      return typeof base==="undefined"?new NativeURL(nextInput):new NativeURL(nextInput,base);
    };
    try{
      Object.getOwnPropertyNames(NativeURL).forEach(function(name){
        if(name==="length"||name==="name"||name==="prototype")return;
        try{Object.defineProperty(RuntimeURL,name,Object.getOwnPropertyDescriptor(NativeURL,name));}catch(_copyError){}
      });
      RuntimeURL.prototype=NativeURL.prototype;
      Object.defineProperty(RuntimeURL,"__applytRuntimeProxyWrapped",{value:true});
      window.URL=RuntimeURL;
    }catch(_error){}
  }
  installStorage("localStorage");
  installStorage("sessionStorage");
  if (typeof window.fetch==="function"&&!window.fetch.__applytRuntimeProxyWrapped){
    var originalFetch=window.fetch;
    var wrappedFetch=function(input,init){
      try{
        if (typeof input==="string") return originalFetch.call(this,proxiedUrl(input),init);
        if (input instanceof URL) return originalFetch.call(this,proxiedUrl(input.toString()),init);
        if (input instanceof Request) {
          var nextUrl=proxiedUrl(input.url);
          if (nextUrl!==input.url) return originalFetch.call(this,new Request(nextUrl,input),init);
        }
      }catch(_error){}
      return originalFetch.call(this,input,init);
    };
    try{Object.defineProperty(wrappedFetch,"__applytRuntimeProxyWrapped",{value:true});}catch(_error){}
    window.fetch=wrappedFetch;
  }
  if (window.XMLHttpRequest&&window.XMLHttpRequest.prototype&&!window.XMLHttpRequest.prototype.open.__applytRuntimeProxyWrapped){
    var originalOpen=window.XMLHttpRequest.prototype.open;
    var wrappedOpen=function(method,url,asyncFlag,user,password){
      return originalOpen.call(this,method,proxiedUrl(String(url)),asyncFlag,user,password);
    };
    try{Object.defineProperty(wrappedOpen,"__applytRuntimeProxyWrapped",{value:true});}catch(_error){}
    window.XMLHttpRequest.prototype.open=wrappedOpen;
  }
})();
</script>`;
}

export function hardenOperatingSystemRuntimeHtml(
  html: string,
  runtimeUrl: string,
  seed: OperatingSystemRuntimeStorageSeed,
  extraImports: Record<string, string> = {},
  navigationSeed: RuntimeNavigationSnapshot | null = null,
): string {
  const proxyBaseUrl = operatingSystemRuntimeProxyBaseUrl(runtimeUrl);
  if (!proxyBaseUrl) return html;

  let output = html
    .replace(/<base\b[^>]*>/gi, "")
    .replace(/<script\b[^>]*data-preview-inject[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<script\b[^>]*src=(["'])[^"']*@base44\/vite-plugin\/dist\/injections\/[^"']*\1[^>]*><\/script>/gi, "")
    .replace(/<script\b[^>]*>(?:(?!<\/script>)[\s\S])*?(?:setupVisualEditAgent|sandbox-bridge)(?:(?!<\/script>)[\s\S])*?<\/script>/gi, "")
    .replace(
      /\b(src|href|action|poster|data-src|data-href)=("([^"]*)"|'([^']*)')/gi,
      (_match, attr: string, quoted: string, doubleValue: string | undefined, singleValue: string | undefined) => {
        const quote = quoted.startsWith("'") ? "'" : "\"";
        const value = doubleValue ?? singleValue ?? "";
        return `${attr}=${quote}${toOperatingSystemRuntimeProxyUrl(value, proxyBaseUrl)}${quote}`;
      },
    )
    .replace(
      /\b(srcset)=("([^"]*)"|'([^']*)')/gi,
      (_match, attr: string, quoted: string, doubleValue: string | undefined, singleValue: string | undefined) => {
        const quote = quoted.startsWith("'") ? "'" : "\"";
        const value = doubleValue ?? singleValue ?? "";
        return `${attr}=${quote}${rewriteSrcset(value, proxyBaseUrl)}${quote}`;
      },
    );

  const injection = `<base href="${proxyBaseUrl}/">${runtimeImportMapScript(proxyBaseUrl, extraImports)}${runtimeLayoutCompatibilityStyle()}${runtimeNavigationScript(proxyBaseUrl)}${runtimeBootstrapScript(proxyBaseUrl, seed)}`;
  if (/<head\b[^>]*>/i.test(output)) {
    output = output.replace(/<head\b([^>]*)>/i, `<head$1>${injection}`);
  } else {
    output = `${injection}${output}`;
  }
  return injectRuntimeNavigationSeed(output, navigationSeed);
}

export function hasOperatingSystemRuntimeServerShim(html: string): boolean {
  return /data-applyt-storage-shim=(?:"true"|'true')/i.test(html);
}

export function operatingSystemRuntimeModuleScriptSpecifiers(html: string): string[] {
  const specifiers = new Set<string>();
  html.replace(
    /<script\b[^>]*\btype=(["'])module\1[^>]*\bsrc=(["'])([^"']+)\2[^>]*>/gi,
    (_match, _typeQuote: string, _srcQuote: string, src: string) => {
      specifiers.add(src);
      return _match;
    },
  );
  html.replace(
    /<script\b[^>]*\bsrc=(["'])([^"']+)\1[^>]*\btype=(["'])module\3[^>]*>/gi,
    (_match, _srcQuote: string, src: string) => {
      specifiers.add(src);
      return _match;
    },
  );
  return [...specifiers];
}

export function operatingSystemRuntimeImportSpecifiers(source: string): string[] {
  const specifiers = new Set<string>();
  const patterns = [
    /\bimport\s+(?:[^"'`]*?\s+from\s*)?["'`]([^"'`]+)["'`]/g,
    /\bimport\s*\(\s*["'`]([^"'`]+)["'`]\s*\)/g,
    /\bfrom\s+["'`]([^"'`]+)["'`]/g,
  ];
  for (const pattern of patterns) {
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(source))) {
      if (match[1]) specifiers.add(match[1]);
    }
  }
  return [...specifiers];
}

export function rewriteOperatingSystemRuntimeSourceFallback(source: string, proxyBaseUrl: string): string {
  return rewriteRuntimeNavigationSource(source
    .replace(/\bBrowserRouter\b/g, "MemoryRouter")
    .replace(
      /(["'`])(\/(?:@[^"'`\s]*|api\/[^"'`\s]*|app\/[^"'`\s]*|assets\/[^"'`\s]*|node_modules\/[^"'`\s]*|src\/[^"'`\s]*))\1/g,
      (_match, quote: string, rootRelativePath: string) => (
        `${quote}${toOperatingSystemRuntimeProxyUrl(rootRelativePath, proxyBaseUrl)}${quote}`
      ),
    )
    .replace(
      /\b(serverUrl\s*:\s*)(["'`])\2/g,
      (_match, prefix: string, quote: string) => `${prefix}${quote}${proxyBaseUrl}${quote}`,
    ), proxyBaseUrl);
}

function sourceDataUrl(source: string): string {
  return `data:text/javascript;charset=utf-8,${encodeURIComponent(source)}`;
}

export async function operatingSystemRuntimeFallbackImports(
  html: string,
  runtimeUrl: string,
  fetchText: (url: string) => Promise<string> = async (url) => {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) throw new Error(`Runtime source returned HTTP ${response.status}`);
    return await response.text();
  },
): Promise<Record<string, string>> {
  if (/data-applyt-runtime-navigation-source=(?:"true"|'true')/i.test(html)) return {};
  const hasServerShim = hasOperatingSystemRuntimeServerShim(html);
  const proxyBaseUrl = operatingSystemRuntimeProxyBaseUrl(runtimeUrl);
  if (!proxyBaseUrl) return {};

  const imports: Record<string, string> = {};
  const mainSpecifiers = operatingSystemRuntimeModuleScriptSpecifiers(html)
    .filter((specifier) => /\/src\/main\.[cm]?[jt]sx?(?:[?#]|$)/i.test(specifier));

  for (const mainSpecifier of mainSpecifiers.slice(0, 2)) {
    const mainUrl = toOperatingSystemRuntimeProxyUrl(mainSpecifier, proxyBaseUrl);
    if (mainUrl === mainSpecifier && !mainSpecifier.startsWith(`${proxyBaseUrl}/`)) continue;
    let mainSource = "";
    try {
      mainSource = await fetchText(mainUrl);
    } catch {
      continue;
    }

    const mainImports = operatingSystemRuntimeImportSpecifiers(mainSource);
    let reactSpecifier = mainImports.find((specifier) => /\/react(?:\.js)?(?:[?#]|$)/i.test(specifier));
    const appSpecifiers = mainImports.filter((specifier) => /\/(?:src|app)\/App\.[cm]?[jt]sx?(?:[?#]|$)/i.test(specifier));
    // Some templates keep their router in pages/index rather than App. Read a
    // bounded source graph; these are the same ticket-scoped modules the frame
    // will load, and no generated source or record is modified.
    const visited = new Set<string>();
    for (let cursor = 0; cursor < appSpecifiers.length && cursor < 12; cursor += 1) {
      const appSpecifier = appSpecifiers[cursor];
      if (visited.has(appSpecifier)) continue;
      visited.add(appSpecifier);
      const appUrl = toOperatingSystemRuntimeProxyUrl(appSpecifier, proxyBaseUrl);
      if (appUrl === appSpecifier && !appSpecifier.startsWith(`${proxyBaseUrl}/`)) continue;
      let appSource = "";
      try {
        appSource = await fetchText(appUrl);
      } catch {
        continue;
      }
      if (appSource.includes("applyt-native-router")) break;
      const specifiers = operatingSystemRuntimeImportSpecifiers(appSource);
      reactSpecifier ??= specifiers.find((specifier) => /\/react(?:\.js)?(?:[?#]|$)/i.test(specifier));
      const routerSpecifiers = specifiers.filter((specifier) => /\/(?:react-router-dom|react-router)(?:\.js)?(?:[?#]|$)/i.test(specifier));
      for (const routerSpecifier of routerSpecifiers) {
        const routerUrl = toOperatingSystemRuntimeProxyUrl(routerSpecifier, proxyBaseUrl);
        // Standard Vite dependency modules expose React in the same dependency
        // directory. Prefer the exact React import from main/App when available.
        const reactUrl = reactSpecifier
          ? toOperatingSystemRuntimeProxyUrl(reactSpecifier, proxyBaseUrl)
          : routerUrl.replace(/react-router(?:-dom)?(?=\.js(?:[?#]|$))/, "react");
        if (reactUrl === routerUrl || !routerUrl.startsWith(`${proxyBaseUrl}/`) || !reactUrl.startsWith(`${proxyBaseUrl}/`)) continue;
        const dataUrl = sourceDataUrl(runtimeRouterModule(routerUrl, reactUrl));
        imports[routerSpecifier] = dataUrl;
        imports[routerUrl] = dataUrl;
      }
      if (!hasServerShim && /\bBrowserRouter\b|\bserverUrl\s*:\s*(["'`])\1/.test(appSource)) {
        const dataUrl = sourceDataUrl(rewriteOperatingSystemRuntimeSourceFallback(appSource, proxyBaseUrl));
        imports[appSpecifier] = dataUrl;
        imports[appUrl] = dataUrl;
      }
      if (routerSpecifiers.length) break;
      appSpecifiers.push(...specifiers.filter((specifier) => /\/(?:src|app)\/[^?#]+\.[cm]?[jt]sx?(?:[?#]|$)/i.test(specifier)
        && !/\/(?:ui|api)\//i.test(specifier) && !visited.has(specifier)));
    }
  }

  return imports;
}
